/**
 * GLM予測機能のユーティリティ関数
 *
 * 学習済みGLMモデルを使用して新しいデータに対する予測を実行する
 */

import type { TrainedModel, GLMParameters } from '../../types/model.types';
import type { DataSet, DerivedDataSet } from '../../types/dataset.types';
import type { ColumnSchema, DataValue } from '../../types/core.types';
import { createDatasetId, createColumnId, createTransformOperationId, createColumnName } from '../../types';
import { getLinkFunction } from './link-functions';
import { DatasetAccessor } from './DatasetAccessor';
import { hasData } from '../../types/dataset.types';
import logger from './logger';

/**
 * データセットがGLM予測と互換性があるかチェックする
 *
 * @param model 学習済みGLMモデル
 * @param dataset チェック対象のデータセット
 * @param trainingDataset トレーニングデータセット（列IDから列名を解決するため）
 * @returns 互換性がある場合true
 */
export function isDatasetCompatible(
  model: TrainedModel,
  dataset: DataSet,
  trainingDataset: DataSet
): boolean {
  logger.assert(model.type === 'glm', 'Model must be a GLM model');

  // モデルの特徴量リスト（列ID）を取得
  const requiredFeatureIds = model.metadata.features;

  // トレーニングデータセットから列IDを列名に変換
  const requiredFeatureNames: string[] = [];
  for (const featureId of requiredFeatureIds) {
    const col = trainingDataset.schema.columns.find(c => c.id === featureId);
    if (!col) {
      logger.warn(`[GLM Prediction] Feature column ${featureId} not found in training dataset`);
      return false;
    }
    requiredFeatureNames.push(col.name);
  }

  // 予測データセットのスキーマから列名→列スキーマのマップを作成
  const columnNameToSchema = new Map<string, ColumnSchema>();
  for (const col of dataset.schema.columns) {
    columnNameToSchema.set(col.name, col);
  }

  // 全ての特徴量が存在し、数値型であることを確認
  for (const featureName of requiredFeatureNames) {
    const column = columnNameToSchema.get(featureName);

    if (!column) {
      logger.debug(`[GLM Prediction] Missing feature column: ${featureName}`);
      return false;
    }

    // 数値型であることを確認
    if (column.type !== 'int64' && column.type !== 'float64') {
      logger.debug(`[GLM Prediction] Feature ${featureName} is not numeric: ${column.type}`);
      return false;
    }
  }

  return true;
}

/**
 * 標準正規分布の分位点を計算する
 *
 * @param p 確率（0-1）
 * @returns z値
 */
function getZQuantile(p: number): number {
  // 一般的な信頼水準のz値を事前計算
  const commonZValues: Record<number, number> = {
    90: 1.645,
    95: 1.96,
    99: 2.576,
    99.9: 3.291
  };

  if (commonZValues[p]) {
    return commonZValues[p];
  }

  // 近似計算（簡易版）
  // 実際にはより精密な計算が必要だが、一般的な値のみ使用する前提
  logger.warn(`[GLM Prediction] Using approximation for uncommon confidence level: ${p}%`);
  return 1.96; // デフォルトで95%を使用
}

/**
 * GLM予測を実行する
 *
 * @param model 学習済みGLMモデル
 * @param dataset 予測対象データセット
 * @param trainingDataset トレーニングデータセット（列IDから列名を解決するため）
 * @param config 予測設定
 * @returns 予測結果を含む派生データセット
 */
export function predictGLM(
  model: TrainedModel,
  dataset: DataSet,
  trainingDataset: DataSet,
  config: {
    includeOriginalData: boolean;
    confidenceLevels: number[];
    predictionLevels: number[];
  }
): DerivedDataSet {
  logger.assert(model.type === 'glm', 'Model must be a GLM model');
  logger.assert(isDatasetCompatible(model, dataset, trainingDataset), 'Dataset is not compatible with the model');

  const params = model.parameters as GLMParameters;
  const linkFunc = getLinkFunction(params.link);

  // データセットにdataがない場合はエラー
  if (!hasData(dataset)) {
    throw new Error('Dataset must have data for prediction');
  }

  const accessor = new DatasetAccessor(dataset);
  const n = dataset.rowCount ?? 0;

  logger.info(`[GLM Prediction] Starting prediction: n=${n}, model=${model.name}`);

  // トレーニングデータセットから列IDを列名に変換
  const featureNames: string[] = [];
  for (const featureId of model.metadata.features) {
    const col = trainingDataset.schema.columns.find(c => c.id === featureId);
    if (!col) {
      throw new Error(`Feature column ${featureId} not found in training dataset`);
    }
    featureNames.push(col.name);
  }

  // 予測データセットから列名に対応する列IDを取得
  const featureColumnIds: string[] = [];
  const columnNameToId = new Map<string, string>();
  for (const col of dataset.schema.columns) {
    columnNameToId.set(col.name, col.id);
  }

  for (const featureName of featureNames) {
    const columnId = columnNameToId.get(featureName);
    if (!columnId) {
      throw new Error(`Feature column not found: ${featureName}`);
    }
    featureColumnIds.push(columnId);
  }

  // 切片を含むかどうか
  const includeIntercept = params.intercept !== undefined;
  const pWithIntercept = includeIntercept ? params.coefficients.length : params.coefficients.length;

  // 予測値、線形予測子、標準誤差を計算
  const linearPredictors: (number | null)[] = [];
  const predictedValues: (number | null)[] = [];
  const standardErrors: (number | null)[] = [];

  for (let i = 0; i < n; i++) {
    // 特徴量ベクトルを構築（切片を含む）
    const x: number[] = includeIntercept ? [1] : [];
    let hasMissing = false;

    for (const colId of featureColumnIds) {
      const column = dataset.schema.columns.find(c => c.id === colId);
      if (!column) {
        throw new Error(`Column ${colId} not found in dataset`);
      }
      const value = accessor.getValue(i, column.name);
      if (value === null || value === undefined) {
        hasMissing = true;
        break;
      }
      x.push(value as number);
    }

    // 欠損値がある場合はnull
    if (hasMissing) {
      linearPredictors.push(null);
      predictedValues.push(null);
      standardErrors.push(null);
      continue;
    }

    // 線形予測子 η = Xβ
    let eta = 0;
    for (let j = 0; j < pWithIntercept; j++) {
      eta += x[j] * params.coefficients[j];
    }

    // 予測値 μ = g⁻¹(η)
    const mu = linkFunc.inverse(eta);

    // 標準誤差 SE(η) = √(x^T * Vcov * x)
    let variance = 0;
    for (let j = 0; j < pWithIntercept; j++) {
      for (let k = 0; k < pWithIntercept; k++) {
        variance += x[j] * params.covarianceMatrix[j][k] * x[k];
      }
    }
    const se = Math.sqrt(Math.abs(variance));

    linearPredictors.push(eta);
    predictedValues.push(mu);
    standardErrors.push(se);
  }

  // 信頼区間と予測区間を計算
  const confidenceIntervals: Record<string, (number | null)[]> = {};
  const predictionIntervals: Record<string, (number | null)[]> = {};

  const dispersion = params.diagnosticSummary.dispersionParameter;

  // 各信頼水準について計算
  for (const level of config.confidenceLevels) {
    const z = getZQuantile(level);
    const lowerKey = `ci_${level}_lower`;
    const upperKey = `ci_${level}_upper`;

    confidenceIntervals[lowerKey] = [];
    confidenceIntervals[upperKey] = [];

    for (let i = 0; i < n; i++) {
      if (linearPredictors[i] === null || standardErrors[i] === null) {
        confidenceIntervals[lowerKey].push(null);
        confidenceIntervals[upperKey].push(null);
        continue;
      }

      const eta = linearPredictors[i]!;
      const se = standardErrors[i]!;

      // 線形予測子スケールで区間を計算
      const etaLower = eta - z * se;
      const etaUpper = eta + z * se;

      // 応答スケールに変換
      const muLower = linkFunc.inverse(etaLower);
      const muUpper = linkFunc.inverse(etaUpper);

      confidenceIntervals[lowerKey].push(muLower);
      confidenceIntervals[upperKey].push(muUpper);
    }
  }

  // 予測区間を計算
  for (const level of config.predictionLevels) {
    const z = getZQuantile(level);
    const lowerKey = `pi_${level}_lower`;
    const upperKey = `pi_${level}_upper`;

    predictionIntervals[lowerKey] = [];
    predictionIntervals[upperKey] = [];

    for (let i = 0; i < n; i++) {
      if (linearPredictors[i] === null || standardErrors[i] === null) {
        predictionIntervals[lowerKey].push(null);
        predictionIntervals[upperKey].push(null);
        continue;
      }

      const eta = linearPredictors[i]!;
      const se = standardErrors[i]!;

      // 予測区間は分散パラメータを加える
      const sePred = Math.sqrt(se * se + dispersion);

      // 線形予測子スケールで区間を計算
      const etaLower = eta - z * sePred;
      const etaUpper = eta + z * sePred;

      // 応答スケールに変換
      const muLower = linkFunc.inverse(etaLower);
      const muUpper = linkFunc.inverse(etaUpper);

      predictionIntervals[lowerKey].push(muLower);
      predictionIntervals[upperKey].push(muUpper);
    }
  }

  // 派生データセットを構築
  const newColumns: ColumnSchema[] = [];
  const newData: Record<string, DataValue[]> = {};

  // 元データを含める場合
  if (config.includeOriginalData) {
    for (const col of dataset.schema.columns) {
      newColumns.push({ ...col });
      newData[col.id] = [...dataset.data[col.id]];
    }
  }

  // 予測関連の列を追加
  const ratioScale = { scale: 'ratio' as const, inferredAutomatically: false };

  const predCol = createColumnId('glm_predicted');
  newColumns.push({
    id: predCol,
    name: createColumnName('glm_predicted'),
    type: 'float64',
    nullable: true,
    scale: ratioScale
  });
  newData[predCol] = predictedValues;

  const lpCol = createColumnId('glm_linear_predictor');
  newColumns.push({
    id: lpCol,
    name: createColumnName('glm_linear_predictor'),
    type: 'float64',
    nullable: true,
    scale: ratioScale
  });
  newData[lpCol] = linearPredictors;

  const seCol = createColumnId('glm_se');
  newColumns.push({
    id: seCol,
    name: createColumnName('glm_se'),
    type: 'float64',
    nullable: true,
    scale: ratioScale
  });
  newData[seCol] = standardErrors;

  // 信頼区間の列を追加
  for (const [key, values] of Object.entries(confidenceIntervals)) {
    const colId = createColumnId(`glm_${key}`);
    newColumns.push({
      id: colId,
      name: createColumnName(`glm_${key}`),
      type: 'float64',
      nullable: true,
      scale: ratioScale
    });
    newData[colId] = values;
  }

  // 予測区間の列を追加
  for (const [key, values] of Object.entries(predictionIntervals)) {
    const colId = createColumnId(`glm_${key}`);
    newColumns.push({
      id: colId,
      name: createColumnName(`glm_${key}`),
      type: 'float64',
      nullable: true,
      scale: ratioScale
    });
    newData[colId] = values;
  }

  // 欠損値がある行数をカウント
  const nMissing = linearPredictors.filter(v => v === null).length;

  logger.info(`[GLM Prediction] Prediction completed: ${n} rows processed, ${nMissing} rows with missing values`);

  // 派生データセットを返す
  const derivedDataset: DerivedDataSet = {
    id: createDatasetId(crypto.randomUUID()),
    name: `GLM Predictions: ${model.name}`,
    schema: {
      columns: newColumns
    },
    rowCount: n,
    type: 'derived',
    parentDatasetIds: [dataset.id],
    operation: {
      id: createTransformOperationId(crypto.randomUUID()),
      type: 'glm_prediction',
      modelId: model.id,
      targetDatasetId: dataset.id,
      confidenceLevels: config.confidenceLevels,
      predictionLevels: config.predictionLevels,
      description: `GLM prediction using model: ${model.name}`
    },
    data: newData,
    metadata: {
      createdAt: new Date().toISOString(),
      description: `GLM predictions using model: ${model.name} on dataset: ${dataset.name}`,
      basedOn: [dataset.id]
    }
  };

  return derivedDataset;
}
